import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { Claim } from '@Models';
import { RootState } from '@Store';
import { addUserMetadata } from './awsRum';

interface RumMetadataSetterProps {
  clientName?: string;
  email?: string;
  userName?: string;
  planNameFull?: string;
}

/**
 * Component that observes Redux state and sends user metadata to AWS RUM.
 * This separates analytics concerns from Redux operations, avoiding timing issues.
 */
const RumMetadataSetter = ({ clientName, email, userName, planNameFull }: RumMetadataSetterProps) => {
  // Track what we've already sent to avoid unnecessary calls
  const sentMetadataRef = useRef<{
    clientName?: string;
    email?: string;
    userName?: string;
    planNameFull?: string;
  }>({});

  // Send email and userName when available (from authorization)
  useEffect(() => {
    const hasNewEmail = email && email !== sentMetadataRef.current.email;
    const hasNewUserName = userName && userName !== sentMetadataRef.current.userName;

    if (hasNewEmail || hasNewUserName) {
      addUserMetadata({
        email: email || undefined,
        userName: userName || undefined,
      });
      sentMetadataRef.current.email = email;
      sentMetadataRef.current.userName = userName;
    }
  }, [email, userName]);

  // Send clientName and planNameFull when available (both from configuration,
  // same Redux slice, so they resolve together — emit in one call).
  useEffect(() => {
    const hasNewClientName = clientName && clientName !== sentMetadataRef.current.clientName;
    const hasNewPlan = planNameFull && planNameFull !== sentMetadataRef.current.planNameFull;

    if (hasNewClientName || hasNewPlan) {
      addUserMetadata({
        clientName: clientName || undefined,
        planNameFull: planNameFull || undefined,
      });
      sentMetadataRef.current.clientName = clientName;
      sentMetadataRef.current.planNameFull = planNameFull;
    }
  }, [clientName, planNameFull]);

  return null;
};

const mapStateToProps = (state: RootState) => {
  const { authorization, client } = state;
  const { claims } = authorization || {};
  const { configuration } = client || {};

  return {
    clientName: configuration?.clientName,
    planNameFull: configuration?.planNameFull || undefined,
    email: claims?.[Claim.Email] || claims?.[Claim.GS1Email] || undefined,
    userName: claims?.[Claim.UserDisplayName] || undefined,
  };
};

export default connect(mapStateToProps)(RumMetadataSetter);
